import { Stack, Typography, Button, TextField, Tooltip } from "@mui/material";
import Icon, {
  BiggerIcon,
} from "../../common/components/fundamentals/Icon";
import { OrangeOnHoverStack } from "../../common/styles/Box.styles";

import { useState } from "react";
import { MediumText } from "../../common/styles/Text.styles";
import { borderStyle, iconGap, smallGap } from "../../styles/consts";
import { formatDateFromDatabase } from "../../utils";
import alqemiDataService from "../../api/AlqemiDataService";
import { useSnackbar } from "../../common/context/SnackbarProvider";

export default function CandidateCard({
  candidate,
  handleDelete,
  handleDateChange,
  handleEditDate,
  selectedDate,
  onPriorityUpdate,
}: {
  candidate: any;
  handleDelete: (id: string) => void;
  handleDateChange: (e: any) => void;
  handleEditDate: (id: string, newDate: any) => void;
  selectedDate: string;
  onPriorityUpdate?: (id: string, priority: number) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPriority, setIsEditingPriority] = useState(false);
  const [priorityValue, setPriorityValue] = useState(candidate.priority || 100);
  const { showSnackbar } = useSnackbar();

  const handleSaveClick = async () => {
    await handleEditDate(candidate.id, selectedDate);
    setIsEditing(false);
  };

  const handlePrioritySave = async () => {
    try {
      const idToUse = candidate.consultant_id || candidate.id;
      await alqemiDataService.updateCandidatePriority(idToUse, priorityValue);
      showSnackbar("Priority updated successfully", false, true);
      if (onPriorityUpdate) {
        onPriorityUpdate(candidate.id, priorityValue);
      }
      setIsEditingPriority(false);
    } catch (error) {
      showSnackbar("Failed to update priority", true, true);
    }
  };

  return (
    <Stack
      sx={{
        border: borderStyle,
        width: "20rem",
        padding: iconGap,
        flexDirection: "row",
        gap: smallGap,
      }}
    >
      <Stack
        component={"img"}
        src="/pfpTest.jpg"
        sx={{ border: borderStyle, width: "4.5rem", height: "4.5rem" }}
      />
      <Stack
        sx={{
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Stack
          sx={{
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          <MediumText>{candidate.name}</MediumText>
          <OrangeOnHoverStack
            id={`delete-${candidate.name}`}
            sx={{
              "&&": { flexDirection: "column" },
              height: "fit-content",
            }}
            onClick={() => handleDelete(candidate.id)}
          >
            <BiggerIcon name="Trash" />
          </OrangeOnHoverStack>
        </Stack>
        <Typography>{candidate.email}</Typography>
        {isEditing ? (
          <Stack
            sx={{
              width: "100%",
              height: "fit-content",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "end",
            }}
          >
            <Stack
              component={"input"}
              type="date"
              onChange={handleDateChange}
              sx={{
                border: borderStyle,
                height: "1.25rem",
                justifyContent: "center",
              }}
            />

            <Button variant="primary" sx={{ height: "1.25rem" }} onClick={handleSaveClick}>
              Save
            </Button>
          </Stack>
        ) : (
          <Stack
            sx={{
              flexDirection: "row",
              gap: iconGap,
              alignItems: "center",
            }}
          >
            <Typography>Availability:</Typography>
            <OrangeOnHoverStack
              id="date"
              sx={{ flexDirection: "row", gap: iconGap, alignItems: "center" }}
              onClick={() => setIsEditing(true)}
            >
              <Typography>
                {formatDateFromDatabase(candidate.from_date)}
              </Typography>
              <Icon name="Edit" />
            </OrangeOnHoverStack>
          </Stack>
        )}
        
        {/* Priority Section */}
        <Stack
          sx={{
            flexDirection: isEditingPriority ? "column" : "row",
            gap: iconGap,
            alignItems: isEditingPriority ? "flex-start" : "center",
            marginTop: "0.25rem",
            width: "100%",
          }}
        >
          <Tooltip 
            title="Lower number = higher priority. When jobs are matched, consultants with lower priority numbers get notified first (1 = highest priority, 100 = default)"
            placement="top"
          >
            <Typography sx={{ cursor: "help", flexShrink: 0 }}>Priority:</Typography>
          </Tooltip>
          {isEditingPriority ? (
            <Stack
              sx={{
                flexDirection: "column",
                gap: "0.25rem",
                width: "100%",
              }}
            >
              <TextField
                type="number"
                value={priorityValue}
                onChange={(e) => setPriorityValue(parseInt(e.target.value) || 100)}
                inputProps={{ min: 1, max: 1000 }}
                size="small"
                sx={{
                  width: "100%",
                  "& .MuiInputBase-input": {
                    padding: "2px 8px",
                    height: "1.25rem",
                    fontSize: "0.75rem",
                  },
                }}
              />
              <Stack
                sx={{
                  flexDirection: "row",
                  gap: iconGap,
                  justifyContent: "flex-start",
                }}
              >
                <Button
                  variant="primary"
                  sx={{ 
                    height: "1.25rem", 
                    fontSize: "0.75rem",
                    padding: "0 0.5rem",
                    minWidth: "auto",
                  }}
                  onClick={handlePrioritySave}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  sx={{ 
                    height: "1.25rem", 
                    fontSize: "0.75rem",
                    padding: "0 0.5rem",
                    minWidth: "auto",
                  }}
                  onClick={() => {
                    setIsEditingPriority(false);
                    setPriorityValue(candidate.priority || 100);
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          ) : (
            <OrangeOnHoverStack
              sx={{ flexDirection: "row", gap: iconGap, alignItems: "center" }}
              onClick={() => setIsEditingPriority(true)}
            >
              <Typography>{candidate.priority || 100}</Typography>
              <Icon name="Edit" />
            </OrangeOnHoverStack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
}
